// 링크 관리 — 콘솔의 핵심 화면
import { useCallback, useEffect, useState } from "react";
import { linkApi, type Link } from "../api/links.ts";
import { billingApi, type Usage } from "../api/billing.ts";
import { ApiError } from "@taplink/web-shared";
import { LinkCreateForm } from "../components/LinkCreateForm.tsx";
import { LinkList } from "../components/LinkList.tsx";
import { QrDialog } from "../components/QrDialog.tsx";
import { LinkStatsDialog } from "../components/LinkStatsDialog.tsx";
import { LinkEditDialog } from "../components/LinkEditDialog.tsx";
import { UsagePanel } from "../components/UsagePanel.tsx";
import { useAuth } from "@taplink/web-shared";

const PAGE_SIZE = 20;

export function LinksPage() {
  const { me } = useAuth();
  const [links, setLinks] = useState<Link[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrTarget, setQrTarget] = useState<Link | null>(null);
  const [statsTarget, setStatsTarget] = useState<Link | null>(null);
  const [editTarget, setEditTarget] = useState<Link | null>(null);

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      setLinks(await linkApi.list(targetPage, PAGE_SIZE));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  /** 사용량 조회는 링크 목록과 독립 — 실패해도 본 화면을 막지 않는다 */
  const loadUsage = useCallback(async () => {
    try {
      setUsage(await billingApi.usage());
    } catch {
      setUsage(null);
    }
  }, []);

  useEffect(() => {
    void load(page);
  }, [load, page]);

  useEffect(() => {
    void loadUsage();
  }, [loadUsage]);

  return (
    <>
      {usage && <UsagePanel usage={usage} />}

      <LinkCreateForm
        accessControlEnabled={usage?.features.ACCESS_CONTROL ?? true}
        onCreated={() => {
          // 생성 즉시 남은 한도가 줄어드므로 함께 갱신한다
          void loadUsage();
          // 새 링크는 최신순 첫 페이지에 나타난다
          if (page === 0) void load(0);
          else setPage(0);
        }}
      />

      <section className="panel">
        <h2>내 링크</h2>

        {error && <div className="alert alert-error" role="alert">{error}</div>}
        {loading ? (
          <p role="status">불러오는 중…</p>
        ) : (
          <LinkList
            links={links}
            myAccountId={me?.accountId}
            onChanged={() => {
              // 비활성/삭제는 '활성 링크' 한도에 영향을 준다
              void loadUsage();
              void load(page);
            }}
            onEdit={setEditTarget}
            onShowQr={setQrTarget}
            onShowStats={setStatsTarget}
          />
        )}

        <div className="pager">
          <button
            type="button"
            className="btn-secondary"
            disabled={page === 0 || loading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            이전
          </button>
          <span aria-live="polite">{page + 1} 페이지</span>
          <button
            type="button"
            className="btn-secondary"
            disabled={links.length < PAGE_SIZE || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            다음
          </button>
        </div>
      </section>

      {qrTarget && (
        <QrDialog
          shortKey={qrTarget.shortKey}
          shortUrl={qrTarget.shortUrl}
          onClose={() => setQrTarget(null)}
        />
      )}

      {editTarget && (
        <LinkEditDialog
          link={editTarget}
          accessControlEnabled={usage?.features.ACCESS_CONTROL ?? true}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void load(page);
          }}
        />
      )}

      {statsTarget && (
        <LinkStatsDialog
          shortKey={statsTarget.shortKey}
          shortUrl={statsTarget.shortUrl}
          onClose={() => setStatsTarget(null)}
        />
      )}
    </>
  );
}
